/**
 * Underlay Page - Ondertapijt Maken
 *
 * Werklijst voor het maken van ondertapijten op maat.
 * Items gegroepeerd op aanbevolen rolbreedte (60-240cm in 10cm stappen).
 * Slimme calculator: kortste zijde kleed - 10cm = aanbevolen rol.
 * Uitzondering: webshop-orders (fulfillmentType 'Eigen verzending') worden op de
 * exact bestelde maat gemaakt — geen 10cm marge (zie UNDERLAY_MARGIN_CM).
 * Sortering binnen groep: op afleverdatum (urgent eerst).
 * Werkbank-render (fase 2): rolbreedte als groepskop, maten mono, snijplan
 * als feitenvakken, tik op de rij = klaar (met 5 s ongedaan maken).
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { WbButton, WbChip, WbFactRow, WbFlag, WbListRow, WbSectionKop, WbState } from '../components/wb';
import { useHeader } from '../stores';
import { salesforceClient } from '../lib/salesforce';
import type { UnderlayItem } from '../lib/salesforce';


// Marge tussen kleed en ondertapijt: 5cm per kant = 10cm totaal, zodat het
// ondertapijt makkelijk ONDER het kleed te plaatsen is.
// Webshop-orders (eigen verzending) worden op de EXACT bestelde maat gemaakt —
// daar is geen kleed om onder te leggen, dus marge = 0.
const UNDERLAY_MARGIN_CM = 10;
const WEBSHOP_MARGIN_CM = 0;

/** De marge die voor dit item geldt: webshop-orders exact, kleden met 10cm marge. */
function marginForItem(item: UnderlayItem): number {
  return item.fulfillmentType === 'Eigen verzending' ? WEBSHOP_MARGIN_CM : UNDERLAY_MARGIN_CM;
}

// Polling interval for real-time updates (30 seconds)
const POLLING_INTERVAL_MS = 30000;

// Track recently toggled items to prevent server overwriting optimistic updates
const RECENT_TOGGLE_GRACE_PERIOD_MS = 5000;

// Undo delay in milliseconds (5 seconds)
const UNDO_DELAY_MS = 5000;

/**
 * Cut plan for an underlay item.
 * - 1-delig: single roll, cut to length
 * - 2-delig: two different rolls side by side, cut to same length
 *   Roll widths add up exactly to the underlay width (no overlap).
 */
interface CutPlan {
  pieces: number;          // 1 of 2 (handmatige override kan dit forceren)
  rolls: number[];        // roll widths (descending), e.g. [180, 170]
  cutLength: number;       // length to cut from each roll
  underlayLength: number;  // total underlay length
  underlayWidth: number;   // total underlay width
  isManual?: boolean;      // true = handmatig aangepast door kantoor (office-app)
}

function calculateCutPlan(length: number | null, width: number | null, isRound?: boolean, diameter?: number | null, margin: number = UNDERLAY_MARGIN_CM): CutPlan | null {
  // Round rugs: single piece
  if (isRound && diameter) {
    const d = Math.max(0, diameter - margin);
    const roll = Math.min(240, Math.max(60, Math.ceil(d / 10) * 10));
    return { pieces: 1, rolls: [roll], cutLength: d, underlayLength: d, underlayWidth: d };
  }

  if (!length || !width) return null;

  const underlayLength = Math.max(length, width) - margin; // longer side → cut length
  const underlayWidth = Math.min(length, width) - margin;  // shorter side → roll width(s)

  if (underlayWidth <= 240) {
    // Single piece
    const roll = Math.max(60, Math.ceil(underlayWidth / 10) * 10);
    return { pieces: 1, rolls: [roll], cutLength: underlayLength, underlayLength, underlayWidth };
  }

  // 2-delig: split width into two rolls that add up exactly
  const half = underlayWidth / 2;
  const roll1 = Math.ceil(half / 10) * 10;  // round up
  const roll2 = underlayWidth - roll1;       // remainder = exact fit
  const rolls = [roll1, roll2].sort((a, b) => b - a);

  return { pieces: 2, rolls, cutLength: underlayLength, underlayLength, underlayWidth };
}

/** Parse een handmatige snijplan-override naar rolbreedtes (aflopend), of null. */
function parseOverride(raw: string | null): number[] | null {
  if (!raw) return null;
  const rolls = raw
    .split(',')
    .map((s) => Number(s.trim()))
    .filter((n) => Number.isFinite(n) && n > 0);
  if (rolls.length === 0) return null;
  return rolls.sort((a, b) => b - a);
}

/** Afgeleide snijlengte (langste kleedzijde − marge, of diameter − marge bij rond). */
function afgeleideSnijlengte(length: number | null, width: number | null, isRound?: boolean, diameter?: number | null, margin: number = UNDERLAY_MARGIN_CM): number | null {
  if (isRound && diameter) return Math.max(0, diameter - margin);
  if (!length || !width) return null;
  return Math.max(length, width) - margin;
}

/**
 * Het snijplan dat de medewerker ziet: handmatige override (gezet vanuit de
 * office-app) heeft voorrang op de automatische berekening. De snijlengte blijft
 * altijd afgeleid uit de kleedmaten; de override bepaalt aantal stukken + rolbreedte(s).
 */
function effectiveCutPlan(item: UnderlayItem): CutPlan | null {
  const margin = marginForItem(item);
  const override = parseOverride(item.snijplanOverride);
  if (override) {
    const snij = afgeleideSnijlengte(item.rugLength, item.rugWidth, item.isRound, item.diameter, margin);
    if (snij != null) {
      const underlayWidth = override.reduce((sum, r) => sum + r, 0);
      return {
        pieces: override.length,
        rolls: override,
        cutLength: snij,
        underlayLength: snij,
        underlayWidth,
        isManual: true,
      };
    }
  }
  return calculateCutPlan(item.rugLength, item.rugWidth, item.isRound, item.diameter, margin);
}

// Type for pending undo item
interface PendingUndoItem {
  item: UnderlayItem;
  timeoutId: ReturnType<typeof setTimeout>;
}

export function UnderlayPage() {
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [items, setItems] = useState<UnderlayItem[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Track items pending undo (marked complete but can still be undone)
  const [pendingUndo, setPendingUndo] = useState<Map<string, PendingUndoItem>>(new Map());

  // Track items that were recently toggled locally
  const recentlyToggledRef = useRef<Map<string, number>>(new Map());

  // Track pending API calls to prevent double-clicks
  const [pendingToggle, setPendingToggle] = useState<Set<string>>(new Set());

  // Fetch items
  const fetchItems = useCallback(async (showRefreshSpinner = false) => {
    try {
      if (showRefreshSpinner) {
        setIsRefreshing(true);
      } else {
        setIsLoading(true);
      }
      setError(null);

      const response = await salesforceClient.getUnderlayItems();
      console.log('[UnderlayPage] API response:', response);

      if (response.success && response.data) {
        const data = response.data;

        // Merge server data with local optimistic updates
        const now = Date.now();
        const mergedItems = data.items.map((item: UnderlayItem) => {
          const toggledAt = recentlyToggledRef.current.get(item.itemId);
          if (toggledAt && (now - toggledAt) < RECENT_TOGGLE_GRACE_PERIOD_MS) {
            // Keep local state for recently toggled items
            const currentItem = items.find((i: UnderlayItem) => i.itemId === item.itemId);
            if (currentItem) {
              return currentItem;
            }
          }
          return item;
        });

        setItems(mergedItems);

        // Clean up old entries
        recentlyToggledRef.current.forEach((timestamp, itemId) => {
          if ((now - timestamp) >= RECENT_TOGGLE_GRACE_PERIOD_MS) {
            recentlyToggledRef.current.delete(itemId);
          }
        });
      } else {
        console.error('[UnderlayPage] API error response:', response);
        setError(response.message || 'Kon items niet ophalen');
      }
    } catch (err) {
      console.error('Error fetching underlay items:', err);
      setError(err instanceof Error ? err.message : 'Er ging iets mis bij het laden van items');
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [items]);

  // Initial fetch
  useEffect(() => {
    fetchItems();
  }, []);

  // Polling for real-time updates
  useEffect(() => {
    const interval = setInterval(() => {
      fetchItems(true);
    }, POLLING_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [fetchItems]);

  // Toggle item completion with undo delay
  const handleToggleItem = useCallback((item: UnderlayItem) => {
    if (pendingToggle.has(item.itemId)) return;

    // Optimistic update - remove from list
    setItems(prevItems => prevItems.filter((i: UnderlayItem) => i.itemId !== item.itemId));

    // Track as recently toggled
    recentlyToggledRef.current.set(item.itemId, Date.now());

    // Create timeout for actual API call (5 second delay for undo)
    const timeoutId = setTimeout(async () => {
      // Remove from pending undo
      setPendingUndo(prev => {
        const next = new Map(prev);
        next.delete(item.itemId);
        return next;
      });

      // Mark as pending API call
      setPendingToggle(prev => new Set(prev).add(item.itemId));

      try {
        const response = await salesforceClient.toggleUnderlayComplete({
          itemIds: [item.itemId],
          complete: true,
        });

        if (!response.success) {
          // Revert on error - add back to list
          console.error('[UnderlayPage] Toggle failed:', response);
          setItems(prevItems => [...prevItems, item]);
          recentlyToggledRef.current.delete(item.itemId);
        }
      } catch (err) {
        console.error('[UnderlayPage] Toggle error:', err);
        // Revert on error
        setItems(prevItems => [...prevItems, item]);
        recentlyToggledRef.current.delete(item.itemId);
      } finally {
        setPendingToggle(prev => {
          const next = new Set(prev);
          next.delete(item.itemId);
          return next;
        });
      }
    }, UNDO_DELAY_MS);

    // Add to pending undo list
    setPendingUndo(prev => {
      const next = new Map(prev);
      next.set(item.itemId, { item, timeoutId });
      return next;
    });
  }, [pendingToggle]);

  // Undo handler - cancel the toggle and restore the item
  const handleUndo = useCallback((itemId: string) => {
    const pending = pendingUndo.get(itemId);
    if (!pending) return;

    // Cancel the timeout
    clearTimeout(pending.timeoutId);

    // Remove from pending undo
    setPendingUndo(prev => {
      const next = new Map(prev);
      next.delete(itemId);
      return next;
    });

    // Remove from recently toggled
    recentlyToggledRef.current.delete(itemId);

    // Restore item to list
    setItems(prevItems => {
      // Check if already in list
      if (prevItems.find(i => i.itemId === itemId)) return prevItems;
      return [...prevItems, pending.item];
    });
  }, [pendingUndo]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      pendingUndo.forEach(({ timeoutId }) => clearTimeout(timeoutId));
    };
  }, [pendingUndo]);

  // Group items by primary (largest) roll width
  const groupedItems = useMemo(() => {
    const groups = new Map<number, UnderlayItem[]>();

    items.forEach(item => {
      const plan = effectiveCutPlan(item);
      const rollWidth = plan ? plan.rolls[0]! : 0;
      if (!groups.has(rollWidth)) {
        groups.set(rollWidth, []);
      }
      groups.get(rollWidth)!.push(item);
    });

    // Sort items within each group by delivery date (earliest first, nulls last)
    groups.forEach((groupItems) => {
      groupItems.sort((a, b) => {
        if (!a.deliveryDate && !b.deliveryDate) return 0;
        if (!a.deliveryDate) return 1;
        if (!b.deliveryDate) return -1;
        return new Date(a.deliveryDate).getTime() - new Date(b.deliveryDate).getTime();
      });
    });

    // Sort groups by roll width
    const sortedGroups = Array.from(groups.entries()).sort((a, b) => a[0] - b[0]);

    return sortedGroups;
  }, [items]);

  // Calculate totals
  const totalItems = items.length;

  // Format date
  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    const today = new Date();
    const tomorrow = new Date();
    tomorrow.setDate(today.getDate() + 1);

    if (dateStr === today.toISOString().split('T')[0]) return 'Vandaag';
    if (dateStr === tomorrow.toISOString().split('T')[0]) return 'Morgen';

    return new Intl.DateTimeFormat('nl-NL', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    }).format(date);
  };

  // Check if date is urgent (today or tomorrow)
  const isUrgent = (dateStr: string | null) => {
    if (!dateStr) return false;
    const date = new Date(dateStr);
    const today = new Date();
    const dayAfterTomorrow = new Date();
    dayAfterTomorrow.setDate(today.getDate() + 2);
    return date < dayAfterTomorrow;
  };

  useHeader('Ondertapijt maken', `${totalItems}`);

  return (
    <div className="space-y-4 pb-[140px]">
      <p className="text-sm text-wb-inkt2">
        Gegroepeerd op rolbreedte. Ondertapijt = kleed min 10 cm (5 cm per kant). Webshop-orders (verzenden) op de exact bestelde maat, zonder marge.
      </p>

      {isLoading && <WbState variant="laden" titel="Items laden…" />}
      {!isLoading && error && <WbState variant="fout" titel={error} onOpnieuw={() => fetchItems()} />}
      {!isLoading && !error && items.length === 0 && <WbState variant="leeg" titel="Alles is klaar." tekst="Er zijn geen ondertapijten die gemaakt moeten worden." />}

      {!isLoading && !error && groupedItems.map(([rollWidth, groupItems], gi) => (
        <section key={rollWidth} className="space-y-1.5">
          <WbSectionKop
            label={`Rol ${rollWidth} cm`}
            teller={groupItems.length}
            actie={gi === 0 ? (
              <WbButton variant="link" onClick={() => fetchItems(true)} disabled={isRefreshing}>
                {isRefreshing ? 'Verversen…' : 'Ververs'}
              </WbButton>
            ) : undefined}
          />
          {groupItems.map((item: UnderlayItem) => {
            const plan = effectiveCutPlan(item);
            const urgent = isUrgent(item.deliveryDate);
            // Webshop-order (PostNL): knippen → naar magazijn voor verzending, NIET met een route mee.
            const isVerzending = item.fulfillmentType === 'Eigen verzending';
            const kleed = item.isRound && item.diameter
              ? `Ø ${item.diameter}`
              : item.rugLength && item.rugWidth
                ? `${item.rugLength} × ${item.rugWidth}`
                : '–';
            const onder = plan
              ? (plan.pieces === 1 && item.isRound ? `Ø ${plan.underlayLength}` : `${plan.underlayLength} × ${plan.underlayWidth}`)
              : '–';
            return (
              <div key={item.itemId} className="rounded-wb-lg border-wb border-wb-rand bg-wb-vlak overflow-hidden">
                <WbListRow
                  onClick={() => handleToggleItem(item)}
                  disabled={pendingToggle.has(item.itemId)}
                  hoofd={item.itemName}
                  className="border-0 rounded-none"
                  chips={
                    <>
                      {item.isRound && <WbChip kind="neutraal">Rond</WbChip>}
                      {plan && plan.pieces === 2 && <WbChip kind="wacht">2-delig</WbChip>}
                      {plan && plan.isManual && <WbChip kind="wacht">Handmatig</WbChip>}
                      {isVerzending
                        ? <WbChip kind="nu">Verzenden</WbChip>
                        : item.deliveryDate
                          ? <WbChip kind={urgent ? 'actie' : 'neutraal'}>{formatDate(item.deliveryDate)}</WbChip>
                          : <WbChip kind="neutraal">Niet ingepland</WbChip>}
                    </>
                  }
                  sub={`${item.customerName} · ${item.workOrderName}`}
                  rechts={<span className="font-wb-heading font-bold text-xs uppercase tracking-wide text-wb-inkt2">Klaar ›</span>}
                />
                <div className="px-3 pb-3 space-y-2">
                  <WbFactRow
                    facts={[
                      { label: 'Kleed cm', value: kleed },
                      { label: isVerzending ? 'Ondertapijt exact' : 'Ondertapijt cm', value: onder },
                      { label: 'Snij cm', value: plan ? plan.cutLength : '–' },
                    ]}
                  />
                  {plan && (plan.pieces > 1 || plan.isManual) && (
                    <WbFlag kind={plan.isManual ? 'opmerking' : 'info'} title={plan.isManual ? `Snijplan van kantoor${plan.pieces > 1 ? ` (${plan.pieces} stukken)` : ''}` : 'Snijplan (2 stukken)'}>
                      {plan.rolls.map((r, idx) => (
                        <span key={idx} className="block font-mono">Rol {r} cm → snij {plan.cutLength} cm</span>
                      ))}
                      {plan.pieces > 1 && <span className="block font-mono text-sm text-wb-inkt2">{plan.rolls.join(' + ')} = {plan.underlayWidth} cm</span>}
                    </WbFlag>
                  )}
                  {item.snijplanNotitie && <WbFlag kind="opmerking" title="Notitie voor knipper">{item.snijplanNotitie}</WbFlag>}
                  {item.wasrapport && <WbFlag kind="info" title="Wasrapport">{item.wasrapport}</WbFlag>}
                </div>
              </div>
            );
          })}
        </section>
      ))}

      {isRefreshing && !isLoading && <p className="font-mono text-sm text-wb-inkt2 text-center">Verversen…</p>}

      {/* Ongedaan maken: vaste balk onderin zolang de 5 s lopen */}
      {pendingUndo.size > 0 && (
        <div className="wb-sticky-bar px-3 pb-2 space-y-2">
          {Array.from(pendingUndo.entries()).map(([itemId, { item }]) => (
            <div key={itemId} className="max-w-container mx-auto">
              <WbFlag kind="info" title={`${item.itemName} afgerond`} onClick={() => handleUndo(itemId)}>
                Tik om ongedaan te maken
              </WbFlag>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
